import { supabase } from './supabase';
import logger from '../utils/logger';

/**
 * Get the current user's profile
 * @returns {Object} { data, error }
 */
export const getUserProfile = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      logger.error('Error getting current user:', userError);
      return { data: null, error: userError };
    }

    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) {
      // If profile doesn't exist yet, return empty profile
      if (error.code === 'PGRST116') {
        return { 
          data: { 
            id: user.id, 
            full_name: '', 
            first_name: '', 
            last_name: '', 
            phone: '', 
            shipping_address: null 
          }, 
          error: null 
        };
      }
      logger.error('Error fetching user profile:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    logger.error('Error in getUserProfile:', error);
    return { data: null, error };
  }
};

/**
 * Update or create the current user's profile
 * @param {Object} profileData - The profile data to update
 * @returns {Object} { success, data, error }
 */
export const updateUserProfile = async (profileData) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      logger.error('Error getting current user:', userError);
      return { success: false, data: null, error: userError };
    }

    // Use upsert to handle both insert and update cases.
    // Avoid selecting the row after write to prevent false errors under strict RLS.
    const { error } = await supabase
      .from('user_profiles')
      .upsert(
        {
          id: user.id,
          ...profileData,
          updated_at: new Date().toISOString()
        },
        {
          onConflict: 'id',
          returning: 'minimal'
        }
      );

    if (error) {
      logger.error('Error updating user profile:', error);
      return { success: false, data: null, error };
    }

    logger.debug('User profile updated successfully');
    // We don't return row data from the DB (RLS friendly); echo the input
    return { success: true, data: { id: user.id, ...profileData }, error: null };
  } catch (error) {
    logger.error('Error in updateUserProfile:', error);
    return { success: false, data: null, error };
  }
};

